<template>
  <v-col
    v-if="shouldRender"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <div v-if="mode === 'show'">
      <v-row
        v-if="modelValue.custom[field] && modelValue.custom[field].length > 0"
        dense
      >
        <v-col
          v-if="showIcon && propertyBySchema.icon"
          class="ta-center"
          cols="2"
          md="1"
        >
          <v-icon
            :icon="propertyBySchema.icon"
            class="fs-18"
          />
        </v-col>
        <v-col
          cols="10"
          md="11"
        >
          <div
            v-if="showTitle && propertyBySchema.alias && propertyBySchema.type !== 'boolean'"
            v-text="$bct(propertyBySchema.alias).toUpperCase()"
            class="bc-prelude mb-1"
          />
          <div v-if="propertyBySchema.type === 'array'">
            <TranslatedItems
              v-if="modelValue.custom[field]"
              :items="modelValue.custom[field]"
              classes="bc-body c-black"
            />
          </div>
          <div v-if="propertyBySchema.type === 'boolean'">
            <div
              v-if="propertyBySchema.icon"
              class="fs-16 c-black"
            >
              {{ $bct(propertyBySchema.alias) }}
            </div>
            <v-checkbox
              v-else
              v-model="modelValue.custom[field]"
              :aria-label="$bct(label)"
              :label="$bct(propertyBySchema.alias)"
              :placeholder="$bct(propertyBySchema.alias)"
              :readonly="true"
              class="mt-0"
              hide-details
            />
          </div>
          <div v-if="propertyBySchema.type === 'string'">
            <div class="fs-16 c-black">
              {{ $bct(modelValue.custom[field]) }}
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
    <div v-else>
      <div v-if="propertyBySchema.type === 'array'">
        <v-row dense>
          <v-col
            class="fw-500"
            cols="12"
          >
            {{ $bct(propertyBySchema.alias) }}
          </v-col>
          <v-col>
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="modelValue.custom[field]"
              @blur="handleBlur"
              @update:model-value="handleUpdateModelValue(modelValue)"
              :append-inner-icon="locked ? 'lock' : ''"
              :aria-label="$bct(`Select ${label}`)"
              :density="dense ? 'compact' : undefined"
              :disabled="isReadonly && locked"
              :items="propertyBySchema.items.enum"
              :placeholder="$bct('Select all that apply')"
              :readonly="isReadonly"
              variant="filled"
              hide-details
              multiple
              tile
            />
          </v-col>
        </v-row>
      </div>
      <div v-if="propertyBySchema.type === 'boolean'">
        <v-row dense>
          <v-col>
            <v-checkbox
              v-model="modelValue.custom[field]"
              @blur="handleBlur"
              @update:model-value="handleBlur"
              :aria-label="$bct(label)"
              :density="dense ? 'compact' : undefined"
              :disabled="isReadonly && locked"
              :label="$bct(propertyBySchema.alias)"
              :placeholder="$bct(propertyBySchema.alias)"
              :readonly="isReadonly"
              class="mt-0"
              hide-details
            />
          </v-col>
        </v-row>
      </div>
      <div v-if="propertyBySchema.type === 'string'">
        <v-row dense>
          <v-col
            class="fw-500"
            cols="12"
          >
            {{ $bct(propertyBySchema.alias) }}
          </v-col>
          <template v-if="propertyBySchema.enum?.length > 0">
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="modelValue.custom[field]"
              @blur="handleBlur"
              @update:model-value="handleUpdateModelValue(modelValue)"
              :append-inner-icon="locked ? 'lock' : ''"
              :aria-label="$bct(`Select ${label}`)"
              :density="dense ? 'compact' : undefined"
              :disabled="isReadonly && locked"
              :items="propertyBySchema.enum"
              :readonly="isReadonly"
              variant="filled"
              hide-details
              tile
            />
          </template>
          <template v-else>
            <v-col>
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
              <v-text-field
                v-model="modelValue.custom[field]"
                @blur="handleBlur"
                @update:model-value="handleUpdateModelValue(modelValue)"
                :append-inner-icon="locked ? 'lock' : ''"
                :aria-label="$bct(`Enter ${label}`)"
                :density="dense ? 'compact' : undefined"
                :disabled="isReadonly && locked"
                :readonly="isReadonly"
                variant="filled"
                hide-details
                tile
              />
            </v-col>
          </template>
        </v-row>
      </div>
      <div v-if="propertyBySchema.type === 'integer' || propertyBySchema.type === 'number'">
        <v-row dense>
          <v-col
            class="fw-500"
            cols="12"
          >
            {{ $bct(propertyBySchema.alias) }}
          </v-col>
          <v-col>
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
            <v-text-field
              v-model="modelValue.custom[field]"
              @blur="handleBlur"
              @update:model-value="handleUpdateModelValue(modelValue)"
              :append-inner-icon="locked ? 'lock' : ''"
              :aria-label="$bct(`Enter ${label}`)"
              :density="dense ? 'compact' : undefined"
              :disabled="isReadonly && locked"
              :readonly="isReadonly"
              type="number"
              variant="filled"
              tile
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </v-col>
</template>

<script setup>
import { useStore } from 'vuex';

import propsToRefs from '@/shared/utils/propsToRefs';
import TranslatedItems from '@/shared/components/TranslatedItems.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const props = defineProps({
  field: {
    type: String,
    default: null,
  },
  mode: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: null,
  },
  schemaId: {
    type: String,
    default: null,
  },
  showIcon: {
    type: String,
    default: null,
  },
  showTitle: {
    type: Boolean,
    default: false,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const modelValue = defineModel({ type: Object });

defineEmits(useLabeledField.emits);

const store = useStore();
const $bct = inject('translator');

const { handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { label, locked } = useSchematizedField(...propsToRefs(props, useSchematizedField.paramKeys));

const canEditFields = computed(() => {
  return fieldPermissions.value?.editRoles?.includes(store.state.role);
});

const canViewFields = computed(() => {
  return fieldPermissions.value?.viewRoles?.includes(store.state.role);
});

const fieldPermissions = computed(() => {
  const schemaDef = store.state.schemas[props.schemaId].definition;
  return schemaDef.properties.custom.properties[props.field];
});

const isReadonly = computed(() => {
  return !(!props.readonly && canEditFields.value);
});

const propertyBySchema = computed(() => {
  if (props.field) {
    const schemaProperties =
      store.state.schemas[props.schemaId].definition.properties.custom.properties;
    return props.field.split('.').reduce((value, index) => {
      return value && value[index] ? value[index] : undefined;
    }, schemaProperties);
  }

  return null;
});

const shouldRender = computed(() => {
  return propertyBySchema.value && (canEditFields.value || canViewFields.value);
});

watch(
  () => props.mode,
  (newVal) => {
    if (propertyBySchema.value.type === 'array') {
      modelValue.value.custom[props.field] ||= newVal === 'edit' ? [] : null;
    }
  },
);
</script>
